import React from 'react'
import styled from 'styled-components'
import Checkbox from 'core-system/Checkbox'
import FlexContainer from 'core-system/FlexContainer'
import { RuleField, RuleOperator, ConditionJoin } from './ruleTypes'
import { RuleConditionValue } from './valueUtils'
import ConditionRow, { ConditionRowDensity } from './ConditionRow'
import CombineWithDivider from './CombineWithDivider'

const ToggleRow = styled(FlexContainer)`
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${(p) => p.theme.palette.grey.grey300};
`

export interface RuleLogicEditorProps {
  density?: ConditionRowDensity
  field: RuleField
  operator: RuleOperator
  value: RuleConditionValue
  onFieldChange: (f: RuleField) => void
  onOperatorChange: (o: RuleOperator) => void
  onValueChange: (v: RuleConditionValue) => void
  addSecondCondition: boolean
  onAddSecondConditionChange: (next: boolean) => void
  conditionJoin: ConditionJoin
  onConditionJoinChange: (j: ConditionJoin) => void
  secondaryField: RuleField
  secondaryOperator: RuleOperator
  secondaryValue: RuleConditionValue
  onSecondaryFieldChange: (f: RuleField) => void
  onSecondaryOperatorChange: (o: RuleOperator) => void
  onSecondaryValueChange: (v: RuleConditionValue) => void
  addThirdCondition: boolean
  onAddThirdConditionChange: (next: boolean) => void
  tertiaryConditionJoin: ConditionJoin
  onTertiaryConditionJoinChange: (j: ConditionJoin) => void
  tertiaryField: RuleField
  tertiaryOperator: RuleOperator
  tertiaryValue: RuleConditionValue
  onTertiaryFieldChange: (f: RuleField) => void
  onTertiaryOperatorChange: (o: RuleOperator) => void
  onTertiaryValueChange: (v: RuleConditionValue) => void
}

const RuleLogicEditor: React.FC<RuleLogicEditorProps> = ({
  density = 'comfortable',
  field,
  operator,
  value,
  onFieldChange,
  onOperatorChange,
  onValueChange,
  addSecondCondition,
  onAddSecondConditionChange,
  conditionJoin,
  onConditionJoinChange,
  secondaryField,
  secondaryOperator,
  secondaryValue,
  onSecondaryFieldChange,
  onSecondaryOperatorChange,
  onSecondaryValueChange,
  addThirdCondition,
  onAddThirdConditionChange,
  tertiaryConditionJoin,
  onTertiaryConditionJoinChange,
  tertiaryField,
  tertiaryOperator,
  tertiaryValue,
  onTertiaryFieldChange,
  onTertiaryOperatorChange,
  onTertiaryValueChange,
}) => {
  const secondaryExcludes = addSecondCondition ? [secondaryField] : []
  const tertiaryExcludes =
    addSecondCondition && addThirdCondition ? [tertiaryField] : []
  const primaryExcludes = [...secondaryExcludes, ...tertiaryExcludes]

  return (
    <>
      <ConditionRow
        density={density}
        field={field}
        operator={operator}
        value={value}
        onFieldChange={onFieldChange}
        onOperatorChange={onOperatorChange}
        onValueChange={onValueChange}
        valuePlaceholder='e.g. HQ or employee metadata'
        excludeFields={primaryExcludes}
      />
      <ToggleRow alignItems='center'>
        <Checkbox
          checked={addSecondCondition}
          onClick={() => onAddSecondConditionChange(!addSecondCondition)}
          label='Require a second condition (AND / OR)'
          textVariant='body2'
        />
      </ToggleRow>
      {addSecondCondition && (
        <>
          <CombineWithDivider
            conditionJoin={conditionJoin}
            onConditionJoinChange={onConditionJoinChange}
            density={density}
          />
          <ConditionRow
            density={density}
            field={secondaryField}
            operator={secondaryOperator}
            value={secondaryValue}
            onFieldChange={onSecondaryFieldChange}
            onOperatorChange={onSecondaryOperatorChange}
            onValueChange={onSecondaryValueChange}
            valuePlaceholder='Second value'
            excludeFields={[field, ...(addThirdCondition ? [tertiaryField] : [])]}
          />
          <ToggleRow alignItems='center'>
            <Checkbox
              checked={addThirdCondition}
              onClick={() => onAddThirdConditionChange(!addThirdCondition)}
              label='Require a third condition (AND / OR)'
              textVariant='body2'
            />
          </ToggleRow>
          {addThirdCondition && (
            <>
              <CombineWithDivider
                conditionJoin={tertiaryConditionJoin}
                onConditionJoinChange={onTertiaryConditionJoinChange}
                density={density}
              />
              <ConditionRow
                density={density}
                field={tertiaryField}
                operator={tertiaryOperator}
                value={tertiaryValue}
                onFieldChange={onTertiaryFieldChange}
                onOperatorChange={onTertiaryOperatorChange}
                onValueChange={onTertiaryValueChange}
                valuePlaceholder='Third value'
                excludeFields={[field, secondaryField]}
              />
            </>
          )}
        </>
      )}
    </>
  )
}

export default React.memo(RuleLogicEditor)
